import { DocumentNode } from 'graphql';
import {
  ApolloError,
  OperationVariables,
  TypedDocumentNode,
  useApolloClient,
} from '@apollo/client';
import { QueryResult } from '@apollo/client/react/types/types';
import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

// the watchQuery.subscribe() return result interface is not exposed, defining here to get typing in the hook
interface ISubscription {
  unsubscribe(): void;
}

interface IOptions<TData, TVariables> {
  shouldLoad: boolean;
  variables: TVariables;
  onError: (error: ApolloError) => void;
  onCompleted: (data: TData | undefined) => void;
  shouldPoll?: (data: TData | undefined) => boolean;
  skip?: boolean;
}

// how often to re-execute the query if shouldPoll returns true. In milliseconds
const POLL_INTERVAL_MS = 5000;

/**
 * A hook similar to useQuery and useLazyQuery but loading is controlled using the `shouldLoad` variable instead of
 * calling `load` / `refetch` functions.
 * When `shouldLoad` variable becomes true, the hook executes a query with current `variables`. If the variables
 * change during hook execution, the existing query is canceled and a new one is executed with the new variables.
 *
 * @param shouldLoad - the trigger variable: if true, execute the query with current variables
 * @param shouldPoll - if the function returns true, the hooks starts re-executing the query until it returns false. It
 * takes the data from the query result as argument. It is executed before `onCompleted` callback
 *
 * The rest of the arguments are similar to useQuery hook:
 * https://www.apollographql.com/docs/react/api/react/hooks/#usequery
 * @param query - same as useQuery argument: the GraphQL query
 * @param variables - same as useQuery argument: the arguments to the GraphQL query
 * @param onCompleted - same as useQuery argument: a callback function that's called when your query successfully
 * completes with zero errors
 * @param skip - same as useQuery argument: If true, the query is not executed even if shouldLoad is true
 * @param onError - callback function when get error on query
 */
export const useQueuedQuery = <TData, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  {
    variables,
    onCompleted,
    shouldLoad,
    onError,
    skip,
    shouldPoll = () => false,
  }: IOptions<TData, TVariables>,
): Pick<QueryResult<TData, OperationVariables>, 'data' | 'loading'> => {
  /*
   * public state: it triggers rerender when changed of the components that use this hook
   */
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState<TData | undefined>(undefined);

  /**
   * private state: doesn't trigger rerender of the components using this hook
   */
  const variableRef = useRef<TVariables>(variables);
  const pollingTimerRef = useRef<number>();
  const isFinishingRef = useRef(false);
  const subscriptionRef = useRef<ISubscription | undefined>(undefined);

  // use the Apollo client directly to gain ability to cancel running query and start a new one
  // inspired by https://gist.github.com/brendanmoore/8cd239c5b35983936581b61922b09b2e after googling around
  // what is the best way to cancel and rerun queries
  const client = useApolloClient();

  useEffect(() => {
    // `shouldLoad` is the trigger variable that causes a query to be executed, `skip` will suppress loading
    // even if shouldLoad is set (similar to useQuery contract)
    if (!shouldLoad || skip) {
      return;
    }
    if (isFinishingRef.current) {
      // The effect was triggered during `next` callback of the watched query. Ignore these updates
      // otherwise we risk into entering endless loop or multiple calls to the server
      return;
    }
    if (isLoading) {
      // The effect was triggered while we are still waiting for the result. If the variables have changed,
      // cancel this call and start a new one with the new variables (if they changed). We use "deep equals"
      // to be consistent with `useQuery` hook behaviour
      if (isEqual(variableRef.current, variables)) {
        return;
      }
      // variables has changed, cancel any running query and start a new one
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = undefined;
      // continue with re-execution of the query
    }

    // store a snapshot of the current variables to compare with in the next call
    variableRef.current = variables;
    setLoading(true);
    // use the watchQuery interface to handle query execution.
    // Important! Can't use destructuring as `watchQuery` is an ObservableQuery
    const watchQuery = client.watchQuery({
      query,
      variables: variableRef.current ?? undefined,
    });
    // store a reference to allow cancellation via unsubscribe() method
    subscriptionRef.current = watchQuery.subscribe({
      next: ({ data: newData, partial }) => {
        // we consider success only when the query successfully completes with zero errors
        if (partial) {
          return;
        }
        if (shouldPoll(newData)) {
          // start polling if not polling already
          if (!pollingTimerRef.current) {
            pollingTimerRef.current = window.setInterval(
              () => watchQuery.refetch(),
              POLL_INTERVAL_MS,
            );
          }
        } else {
          // stop any polling if any
          if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = undefined;
          }
          // call the onComplete callback and update the internal state (`data`, `loading`) with the result
          isFinishingRef.current = true;
          onCompleted(newData);
          setData(newData);
          setLoading(false);
          isFinishingRef.current = false;
          // unsubscribe as the next call will create a new subscription
          subscriptionRef.current?.unsubscribe();
          subscriptionRef.current = undefined;
        }
      },
      // I don't know the actual contract if these callbacks, simply logging to see if they are called under
      // a specific condition. The docs are pretty slim
      // https://www.apollographql.com/docs/react/api/core/ObservableQuery/
      start: () => {
        console.info('useQueuedQuery: start');
      },
      complete: () => {
        console.info('useQueuedQuery: complete');
      },
      error: (error) => {
        onError(error);
        console.warn('useQueuedQuery: error', error);
      },
    });
  }, [
    client,
    isLoading,
    onCompleted,
    query,
    shouldLoad,
    shouldPoll,
    skip,
    variables,
    onError,
  ]);

  // cancel any running query or polling on unmount
  useEffect(
    () => () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = undefined;
      }
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = undefined;
    },
    [],
  );

  // subset of the useQuery result for the current needs
  return { data, loading: isLoading };
};
